import { championImageUrl } from '../config';
import type { ChampionSummary } from '../types';

interface Props {
  /** The champion in your selected slot, if any. */
  champion: ChampionSummary | null;
  playerNumber: number;
}

/** A 0-10 stat meter from Riot's champion `info` scores. */
function StatBar({ label, value }: { label: string; value: number | null }) {
  const pct = Math.max(0, Math.min(10, value ?? 0)) * 10;
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <div className="stat__track">
        <div className="stat__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="stat__num">{value ?? '–'}</span>
    </div>
  );
}

/** Shows the champion in the slot you marked as "you" — its stat profile and the
 * anchor for the upcoming starting-runes feature. */
export default function YourChampion({ champion, playerNumber }: Props) {
  const url = champion ? championImageUrl(champion.image) : null;
  return (
    <section className="you-panel">
      <h2>Your pick — player {playerNumber}</h2>
      {champion ? (
        <>
          <div className="you-champ">
            {url && <img src={url} alt={champion.name} width={64} height={64} />}
            <div>
              <div className="you-name">{champion.name}</div>
              {champion.title && <div className="you-title">{champion.title}</div>}
              <div className="you-roles">{champion.roles.join(' · ')}</div>
            </div>
          </div>
          <div className="you-stats">
            <StatBar label="Attack" value={champion.attack} />
            <StatBar label="Magic" value={champion.magic} />
            <StatBar label="Defense" value={champion.defense} />
            <StatBar label="Difficulty" value={champion.difficulty} />
          </div>
        </>
      ) : (
        <p className="you-empty">
          Pick a champion into your slot (player {playerNumber}) to see it here — pick number is set with
          the 1-5 buttons above your team.
        </p>
      )}
      <div className="you-runes">
        <div className="you-runes__head">
          <span className="you-runes__title">Starting runes</span>
          <span className="you-runes__soon">coming soon</span>
        </div>
        <p className="you-runes__note">
          Meta rune pages for your champion will appear here.
        </p>
      </div>
    </section>
  );
}
